/**
 * Site Context Provider
 *
 * Manages the user's site access scope for multi-site RBAC:
 * - Which sites the user can access (from site_roles in JWT/login response)
 * - Active site selection with backend JWT switching
 * - Corporate vs site-level user detection
 *
 * Coordinates with OrgContext (hierarchy data) and PermissionContext (RBAC refresh).
 * Gracefully degrades when Phase 1 backend endpoints are not yet available.
 *
 * SOC 2 Controls:
 * - CC6.1: Site-level access scoping
 * - CC6.2: Role-based site access
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useOrg } from './OrgContext';
import { usePermissions } from './PermissionContext';

const SiteContext = createContext(null);

const CORPORATE_ROLE_NAMES = [
  'admin', 'group_admin', 'corporate_safety_head', 'corporate_ers_head',
  'sustainability_lead', 'governance_lead', 'c_suite', 'board_member',
  'super_admin', 'tenant_admin'
];

const STORAGE_KEY = 'activeSiteId';
const STORAGE_KEY_NAME = 'activeSiteName';
// Sentinel persisted when the user explicitly selects "All Sites" (null).
// Distinguishes an intentional all-sites view from "never selected", so a
// refresh does not silently fall back to the JWT's active_site_id.
const ALL_SITES_SENTINEL = '__ALL__';

export function SiteProvider({ children }) {
  const { token, user, login } = useAuth();
  const { sites: orgSites, setActiveSite: setOrgActiveSite } = useOrg();
  const { refreshPermissions } = usePermissions();

  const [activeSiteId, setActiveSiteId] = useState(null);
  const [activeSiteName, setActiveSiteName] = useState(null);
  const [scopeType, setScopeType] = useState(null);
  const [availableSites, setAvailableSites] = useState([]);
  const [currentSiteRole, setCurrentSiteRole] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Determine if user is corporate-level based on role
  const isCorpUser = scopeType === 'corporate' || (
    !scopeType && user?.role && CORPORATE_ROLE_NAMES.includes(user.role.toLowerCase())
  );

  /**
   * Initialize site context from user data.
   * Reads site_roles from login response if available,
   * otherwise falls back to OrgContext sites.
   */
  useEffect(() => {
    if (!token || !user) {
      setAvailableSites([]);
      setScopeType(null);
      setActiveSiteId(null);
      setActiveSiteName(null);
      setCurrentSiteRole(null);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY_NAME);
      setInitialized(true);
      return;
    }

    // Read site_roles from user object (populated by login response)
    const siteRoles = user.site_roles;
    const userScopeType = user.scope_type;

    if (siteRoles && siteRoles.length > 0) {
      // Backend Phase 1 is ready — use site_roles from JWT/login
      setAvailableSites(siteRoles);
      // Detect corporate scope from: explicit scope_type, site_roles scope_type, or role name
      const hasCorporateSiteRole = siteRoles.some(sr => sr.scope_type === 'corporate');
      const resolvedScope = userScopeType
        || (hasCorporateSiteRole ? 'corporate' : null)
        || (CORPORATE_ROLE_NAMES.includes(user.role?.toLowerCase()) ? 'corporate' : 'site');
      setScopeType(resolvedScope);
    } else {
      // Backend not ready — fallback to OrgContext sites
      const fallbackSites = (orgSites || []).map(s => ({
        site_id: s.id,
        site_name: s.name,
        role: user.role || 'unknown'
      }));
      setAvailableSites(fallbackSites);
      setScopeType(
        CORPORATE_ROLE_NAMES.includes(user.role?.toLowerCase()) ? 'corporate' : 'site'
      );
    }

    // Restore activeSiteId and activeSiteName from localStorage
    const storedSiteId = localStorage.getItem(STORAGE_KEY);
    const storedSiteName = localStorage.getItem(STORAGE_KEY_NAME);
    if (storedSiteId === ALL_SITES_SENTINEL) {
      // User explicitly chose "All Sites" — keep null and do NOT fall back
      // to the JWT's active_site_id (which would revert to a specific site).
      setActiveSiteId(null);
    } else if (storedSiteId && storedSiteId !== 'null') {
      setActiveSiteId(storedSiteId);
      // Restore cached site name immediately to avoid "Loading..." flash
      if (storedSiteName && storedSiteName !== 'null') {
        setActiveSiteName(storedSiteName);
      }
    } else if (user.active_site_id) {
      setActiveSiteId(user.active_site_id);
    }

    setInitialized(true);
  }, [token, user, orgSites]);

  // Derive activeSiteName and currentSiteRole when activeSiteId or availableSites change
  useEffect(() => {
    if (!activeSiteId) {
      setActiveSiteName(null);
      setCurrentSiteRole(null);
      localStorage.removeItem(STORAGE_KEY_NAME);
      return;
    }
    const site = availableSites.find(s => s.site_id === activeSiteId);
    if (site) {
      setActiveSiteName(site.site_name);
      setCurrentSiteRole(site.role || null);
      localStorage.setItem(STORAGE_KEY_NAME, site.site_name || '');
    }
    // If site not found in availableSites, keep existing activeSiteName
    // (may have been set directly from switchSite response or localStorage)
  }, [activeSiteId, availableSites]);

  /**
   * Switch active site.
   * Tries backend JWT switch first; falls back to local-only if unavailable.
   */
  // Pages with unsaved work can register a guard that runs BEFORE the site
  // changes, so the user gets a chance to cancel and keep it. A guard returns
  // true to allow the switch, false to abort. Registered via
  // registerSiteSwitchGuard() below; with none registered, behaviour is
  // unchanged.
  //
  // A guard is a courtesy, not a safety mechanism: a page whose state is
  // site-specific must still reset it when activeSiteId changes, because a
  // guard can be bypassed (force) or fail.
  const switchGuardsRef = useRef(new Set());

  const registerSiteSwitchGuard = useCallback((guard) => {
    switchGuardsRef.current.add(guard);
    return () => switchGuardsRef.current.delete(guard);
  }, []);

  const switchSite = useCallback(async (siteId, { force = false } = {}) => {
    // Allow null to mean "All Sites" for corporate users
    const targetSiteId = siteId || null;

    if (!force) {
      for (const guard of Array.from(switchGuardsRef.current)) {
        try {
          // eslint-disable-next-line no-await-in-loop
          if (await guard(targetSiteId) === false) return false;
        } catch (err) {
          // A broken guard must not trap the user on the current site.
          console.warn('[SiteContext] site-switch guard failed, allowing switch', err);
        }
      }
    }

    setSwitching(true);
    try {
      // Attempt backend switch for new JWT
      const response = await fetch(`${BACKEND_URL}/api/auth/switch-site`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ site_id: targetSiteId })
      });

      if (response.ok) {
        const data = await response.json();
        // Update auth with new JWT
        const newToken = data.access_token || data.token;
        const updatedUser = {
          ...user,
          active_site_id: targetSiteId,
          site_roles: data.site_roles || user.site_roles
        };

        if (newToken) {
          login(newToken, updatedUser, JSON.parse(localStorage.getItem('tenant')));
        }

        // Set site ID and name directly from backend response
        setActiveSiteId(targetSiteId);
        setActiveSiteName(data.active_site_name || null);
        localStorage.setItem(STORAGE_KEY, targetSiteId || ALL_SITES_SENTINEL);
        localStorage.setItem(STORAGE_KEY_NAME, data.active_site_name || '');

        // Update availableSites if backend returned fresh site_roles
        if (data.site_roles) {
          setAvailableSites(data.site_roles);
        }

        // Refresh permissions for new site scope
        try { await refreshPermissions(); } catch (e) { /* non-critical */ }
        try { setOrgActiveSite(targetSiteId); } catch (e) { /* non-critical */ }

        setSwitching(false);
        return true;
      }

      // If endpoint doesn't exist (404/405), fall through to local switch
      if (response.status !== 404 && response.status !== 405) {
        console.warn('[SiteContext] switch-site returned', response.status);
      }
    } catch (err) {
      console.warn('[SiteContext] switch-site endpoint not available, using local-only switching');
    }

    // Local-only fallback
    setActiveSiteId(targetSiteId);
    localStorage.setItem(STORAGE_KEY, targetSiteId || ALL_SITES_SENTINEL);
    // Derive name from availableSites for local fallback
    const fallbackSite = availableSites.find(s => s.site_id === targetSiteId);
    if (fallbackSite) {
      setActiveSiteName(fallbackSite.site_name);
      localStorage.setItem(STORAGE_KEY_NAME, fallbackSite.site_name || '');
    }
    try { setOrgActiveSite(targetSiteId); } catch (e) { /* non-critical */ }
    try { await refreshPermissions(); } catch (e) { /* non-critical */ }

    setSwitching(false);
    return true;
  }, [token, user, login, refreshPermissions, setOrgActiveSite, availableSites, BACKEND_URL]);

  // Auto-select a site for ANY site-scoped user — must call switchSite so the
  // JWT gets active_site_id.
  //
  // This used to fire only when the user had exactly one site. A site-scoped
  // user with several (a Safety Manager assigned to six, say) therefore landed
  // with no selection at all, and every dashboard read answered 400 "site_id
  // required: please select a site to view this dashboard" — correct
  // fail-closed behaviour server-side, but it reached the user as a chart
  // saying "Failed to load chart". Picking their first site is a starting
  // point they can change from the header; showing them a broken page is not.
  // Corporate users are untouched: all-sites is a legitimate scope for them.
  const autoSelectDone = useRef(false);
  useEffect(() => {
    if (!initialized) return;
    if (autoSelectDone.current) return;
    if (!isCorpUser && availableSites.length > 0 && !activeSiteId) {
      autoSelectDone.current = true;
      // force: this is initialisation, not a user-initiated change, so it must
      // not be blocked by a page's unsaved-work guard.
      switchSite(availableSites[0].site_id, { force: true });
    }
  }, [initialized, isCorpUser, availableSites, activeSiteId, switchSite]);

  // Ensure switching is reset after the async flow
  useEffect(() => {
    if (switching) {
      const timeout = setTimeout(() => setSwitching(false), 5000);
      return () => clearTimeout(timeout);
    }
  }, [switching]);

  const value = {
    activeSiteId,
    activeSiteName,
    scopeType,
    isCorpUser,
    availableSites,
    currentSiteRole,
    switching,
    switchSite,
    registerSiteSwitchGuard,
    initialized
  };

  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  );
}

/**
 * Hook to access site context.
 * Returns a safe fallback if used outside SiteProvider (e.g., login page).
 */
export function useSite() {
  const context = useContext(SiteContext);
  if (!context) {
    // Safe fallback for components rendered outside SiteProvider (e.g., login)
    return {
      activeSiteId: null,
      activeSiteName: null,
      scopeType: null,
      isCorpUser: false,
      availableSites: [],
      currentSiteRole: null,
      switching: false,
      switchSite: () => {},
      // No-op unregister, so a component can register a guard unconditionally
      // even when rendered outside the provider.
      registerSiteSwitchGuard: () => () => {},
      initialized: false
    };
  }
  return context;
}
